/**
 * Theme Helper
 * 
 * Helper functions for determining theme, tier, and ambassador status
 * from user tier data structure
 * 
 * @method getThemeFromTier - Extract theme, tier, and ambassador info from tier data
 */

export default {
  /**
     * Extract theme, tier, and ambassador information from tier data
     * Supports three data structures:
     * 1. { Meta: { Tier: {...} } } - AccountMixin structure (plain object)
     * 2. { Tier: {...} } - Gift API structure (plain object)
     * 3. { meta: { tier: TierModel } } - ProfileModel structure (TierModel instance)
     * 
     * @param {Object} tierData - User tier data object
     * @returns {Object} - { theme: string, tier: string, isAmbassador: boolean }
     */
    getThemeFromTier: function(tierData) {
        // Default values
        var defaultResult = {
            theme: 'prestige',
            tier: null,
            isAmbassador: false
        };

        if (!tierData) {
            return defaultResult;
        }

        // Try to get Tier object from different possible structures
        var tierObj = this.extractTierObj(tierData);

        // If no Tier object found, return default
        if (!tierObj) {
            return defaultResult;
        }

        // Extract values from Tier object
        // var tierWeek = tierObj['TIER_END_LAST_WEEK'] || tierObj.tierEndLastWeek;
        // TODO: need to check if tierMonth (UserStory 793073, 792117 Handle UI on Mass & Prestige Level Theme Base to Field "TIER_END_LAST_MONTH")
        var tierMonth = tierObj['TIER_END_LAST_MONTH'] || tierObj.tierEndLastMonth;
        var tier = tierMonth ? tierMonth.toLowerCase() : null;
        var ambassador = tierObj['AMBASSADOR'] || tierObj.ambassador;
        var isAmbassador = ambassador === 'A';

        // Determine theme:
        // - 'mass' if tierMonth is 'M' and not ambassador
        // - 'prestige' otherwise (default or if ambassador)
        var theme = (tierMonth === 'M' && !isAmbassador) ? 'mass' : 'prestige';

        return {
            theme: theme,
            tier: tier,
            isAmbassador: isAmbassador
        };
    },

    /**
     * Extract the raw Tier object from the supported tier data structures.
     * Supports the same three shapes as getThemeFromTier:
     * 1. { Meta: { Tier: {...} } } - AccountMixin structure (plain object)
     * 2. { Tier: {...} } - Gift API structure (plain object)
     * 3. { meta: { tier: TierModel } } - ProfileModel structure (TierModel instance)
     *
     * @param {Object} tierData - User tier data object
     * @returns {Object|null} - Raw Tier object, or null when not found
     */
    extractTierObj: function(tierData) {
        if (!tierData) {
            return null;
        }

        // Structure 1: tierData.Meta.Tier (AccountMixin)
        if (tierData.Meta && tierData.Meta.Tier) {
            return tierData.Meta.Tier;
        }
        // Structure 2: tierData.Tier (Gift API)
        if (tierData.Tier) {
            return tierData.Tier;
        }
        // Structure 3: tierData.meta.tier (ProfileModel - TierModel instance)
        if (tierData.meta && tierData.meta.tier) {
            return tierData.meta.tier;
        }

        return null;
    },

    /**
     * Build the Customer Level Status string from session tier data.
     * No API call is made — the value is derived from the data already in session.
     * (WI #878678 / BR-2, format reference: WI #856566)
     *
     * - Single level  -> raw tier value (e.g. "S")
     * - Ambassador (multi-level) -> tier joined with "|Am" (e.g. "S|Am")
     *
     * NOTE: the ambassador suffix "Am" is the proposed marker from WI #856566
     * and should be confirmed with the developer/BA before UAT (Open Question Q1).
     *
     * @param {Object} tierData - User tier data object (same shapes as getThemeFromTier)
     * @returns {string|null} - Customer Level string, or null when unavailable
     */
    getCustomerLevel: function(tierData) {
        var tierObj = this.extractTierObj(tierData);
        if (!tierObj) {
            return null;
        }

        var levelRaw = tierObj['TIER_END_LAST_MONTH'] || tierObj.tierEndLastMonth;
        if (!levelRaw) {
            return null;
        }

        var ambassador = tierObj['AMBASSADOR'] || tierObj.ambassador;
        var isAmbassador = ambassador === 'A';

        return isAmbassador ? (levelRaw + '|Am') : levelRaw;
    }
};
